"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SelfOrderEvent = exports.SelfOrderActorType = exports.SelfOrderEventType = exports.SelfOrder = exports.SelfOrderLine = exports.SelfOrderLineModifierSnapshot = exports.CashKitchenReleasePolicy = exports.SelfOrderFulfilmentStatus = exports.SelfOrderPaymentMethod = exports.SelfOrderPaymentStatus = exports.SelfOrderCommercialStatus = void 0;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
const terminal_js_1 = require("./terminal.js");
const self_order_menu_js_1 = require("./self-order-menu.js");
/**
 * The durable self-order aggregate (self-order spec v2.3 §6.3–§6.5).
 *
 * Three status fields, deliberately orthogonal (§6.4). "Paid", "preparing" and
 * "ready" are different facts about the same order and must never collapse into
 * one enum: the normal state of every cash order, between the ordering station
 * and the till, is *ready and unpaid*. A single status cannot express that, and
 * a model that cannot express it will be worked around in the UI.
 *
 * Prices, names, tax data and modifier labels are snapshotted into the accepted
 * order, so a later menu edit does not rewrite history.
 */
/* -------------------------------------------------------------------------- */
/* Status                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Commercial life of the order.
 *
 * ```text
 * draft -> submitted -> accepted -> closed
 *                    -> rejected
 * submitted/accepted -> cancelled   (policy + audit only)
 * ```
 *
 * `draft` exists only on the terminal. The platform never stores one: an order
 * arrives at `submitted` or not at all.
 */
exports.SelfOrderCommercialStatus = zod_1.z.enum([
    'draft',
    'submitted',
    'accepted',
    'rejected',
    'cancelled',
    'closed',
]);
/**
 * Payment life, independent of both commercial and fulfilment status.
 *
 * `indeterminate` is the important one and the reason this is not a boolean: the
 * provider was asked and did not answer knowably. It blocks any retry that could
 * double-charge, and is resolved by the adapter's last-transaction query or by a
 * human, never by a timeout deciding on its own.
 */
exports.SelfOrderPaymentStatus = zod_1.z.enum([
    'not_required',
    'pending',
    'authorising',
    'paid',
    'declined',
    'indeterminate',
    'reversal_pending',
    'reversed',
    'refund_pending',
    'refunded',
]);
/**
 * How the customer pays. Separate from status, because "how" and "how far along"
 * are different questions.
 *
 * `cash_at_pickup` is never taken at the ordering station (spec decision 7) — it
 * is taken at the re:form.lite till, which performs the fiscal action and then
 * calls the platform's guarded cash-payment command.
 */
exports.SelfOrderPaymentMethod = zod_1.z.enum(['card_external', 'card_softpos', 'cash_at_pickup']);
/**
 * Preparation and handover life.
 *
 * `not_released` is the gap the release policy governs: a card order enters
 * `queued` only once paid, a cash order either on acceptance or on payment
 * depending on `cashKitchenReleasePolicy`.
 */
exports.SelfOrderFulfilmentStatus = zod_1.z.enum([
    'not_released',
    'queued',
    'preparing',
    'ready',
    'collected',
    'cancelled',
    'no_show',
]);
/**
 * When an unpaid cash order may reach the kitchen.
 *
 * `on_order_acceptance` lets preparation start while the customer walks to the
 * till, which is the point of the format. It is the proposed default but must be
 * confirmed per customer before production (spec §18.11) — it trades food cost
 * on a walk-away against queue time.
 */
exports.CashKitchenReleasePolicy = zod_1.z.enum(['on_order_acceptance', 'on_cash_payment']);
/* -------------------------------------------------------------------------- */
/* Line snapshots                                                             */
/* -------------------------------------------------------------------------- */
/**
 * A modifier as chosen, frozen at acceptance.
 *
 * The label is stored, not referenced. If the menu later renames "Oat milk" the
 * receipt and the kitchen ticket for yesterday's order must still read what the
 * customer actually saw.
 */
exports.SelfOrderLineModifierSnapshot = zod_1.z.object({
    modifierGroupId: zod_1.z.string().min(1),
    modifierOptionId: zod_1.z.string().min(1),
    groupNameSnapshot: zod_1.z.string().min(1).max(512),
    optionLabelSnapshot: zod_1.z.string().min(1).max(512),
    priceDeltaMinor: pricing_js_1.MoneyMinor,
    quantity: zod_1.z.number().int().min(1).default(1),
    preparationText: zod_1.z.string().max(255).optional(),
});
exports.SelfOrderLine = zod_1.z.object({
    id: zod_1.z.string().min(1),
    orderId: zod_1.z.string().min(1),
    catalogItemId: zod_1.z.string().min(1),
    menuItemId: zod_1.z.string().min(1),
    displayNameSnapshot: zod_1.z.string().min(1).max(512),
    quantity: zod_1.z.number().int().min(1),
    unitPriceMinor: pricing_js_1.MoneyMinor,
    modifierTotalMinor: pricing_js_1.MoneyMinor.default('0'),
    lineTotalMinor: pricing_js_1.MoneyMinor,
    /** Frozen for fiscal purposes; the pack's category key, not a rate. */
    taxCategory: zod_1.z.string().min(1).max(32),
    /** Rate as applied, percent with up to 3 decimals, as a string. */
    taxRatePercentSnapshot: zod_1.z.string().regex(/^\d+(\.\d{1,3})?$/),
    taxMinor: pricing_js_1.MoneyMinor.default('0'),
    modifiers: zod_1.z.array(exports.SelfOrderLineModifierSnapshot).default([]),
    /** Kitchen-facing text derived from the modifiers at acceptance. */
    preparationNotes: zod_1.z.string().max(1024).optional(),
    preparationStationTags: zod_1.z.array(zod_1.z.string().min(1).max(64)).default([]),
});
/* -------------------------------------------------------------------------- */
/* The aggregate                                                              */
/* -------------------------------------------------------------------------- */
exports.SelfOrder = zod_1.z
    .object({
    id: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    locationId: zod_1.z.string().min(1),
    terminalId: zod_1.z.string().min(1),
    /** The published menu version the customer actually ordered against. */
    menuVersion: zod_1.z.number().int().min(1),
    /**
     * Created by the terminal before the network call and persisted there, so a
     * retry after a crash is recognisably the same order rather than a second one.
     */
    clientOrderId: zod_1.z.string().min(1).max(64),
    idempotencyKey: zod_1.z.string().min(1).max(128),
    /** The short number the customer and the queue display see. Not the id. */
    displayNumber: zod_1.z.string().min(1).max(16),
    orderType: self_order_menu_js_1.SelfOrderType,
    locale: terminal_js_1.LocaleCode,
    currency: pricing_js_1.CurrencyCode,
    commercialStatus: exports.SelfOrderCommercialStatus,
    paymentStatus: exports.SelfOrderPaymentStatus,
    paymentMethod: exports.SelfOrderPaymentMethod,
    fulfilmentStatus: exports.SelfOrderFulfilmentStatus,
    subtotalMinor: pricing_js_1.MoneyMinor,
    discountMinor: pricing_js_1.MoneyMinor.default('0'),
    taxMinor: pricing_js_1.MoneyMinor.default('0'),
    /**
     * The one authoritative amount. Every customer-facing surface shows this and
     * nothing that looks like it (Amendment 1, D3) — a subtotal row equal to the
     * total reads as an error to the customer and produces a support call.
     */
    totalMinor: pricing_js_1.MoneyMinor,
    lines: zod_1.z.array(exports.SelfOrderLine).min(1),
    createdAt: zod_1.z.string().datetime(),
    submittedAt: zod_1.z.string().datetime().nullable().default(null),
    paidAt: zod_1.z.string().datetime().nullable().default(null),
    readyAt: zod_1.z.string().datetime().nullable().default(null),
    collectedAt: zod_1.z.string().datetime().nullable().default(null),
    /** Optimistic concurrency. Every mutation states the version it expects. */
    version: zod_1.z.number().int().min(0),
})
    .superRefine((order, ctx) => {
    if (order.paymentMethod === 'cash_at_pickup' && order.fulfilmentStatus === 'collected') {
        if (order.paymentStatus !== 'paid') {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['fulfilmentStatus'],
                message: 'an unpaid cash order cannot be collected',
            });
        }
    }
    if (order.paymentStatus === 'paid' && order.paidAt === null) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['paidAt'],
            message: 'a paid order must carry paidAt',
        });
    }
    if (order.commercialStatus !== 'draft' && order.submittedAt === null) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['submittedAt'],
            message: 'a submitted order must carry submittedAt',
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Event history (§6.5)                                                        */
/* -------------------------------------------------------------------------- */
exports.SelfOrderEventType = zod_1.z.enum([
    'ORDER_SUBMITTED',
    'ORDER_ACCEPTED',
    'ORDER_REJECTED',
    'PAYMENT_STARTED',
    'PAYMENT_APPROVED',
    'PAYMENT_DECLINED',
    'PAYMENT_INDETERMINATE',
    'CASH_PAYMENT_ACCEPTED',
    'KITCHEN_RELEASED',
    'PREPARATION_STARTED',
    'ORDER_READY',
    'ORDER_COLLECTED',
    'ORDER_CANCELLED',
    'RECEIPT_PRINTED',
    'RECEIPT_REPRINTED',
    'PRINT_FAILED',
    'ASSISTANCE_REQUESTED',
]);
exports.SelfOrderActorType = zod_1.z.enum([
    'customer',
    'cashier',
    'kitchen',
    'system',
    'integration',
]);
/**
 * Append-only. These rows are the audit trail and are never updated in place —
 * a corrected fact is a new event, not an edited one.
 */
exports.SelfOrderEvent = zod_1.z.object({
    id: zod_1.z.string().min(1),
    orderId: zod_1.z.string().min(1),
    type: exports.SelfOrderEventType,
    /** The order version this event produced, so the timeline can be replayed. */
    orderVersion: zod_1.z.number().int().min(0),
    actorType: exports.SelfOrderActorType,
    actorId: zod_1.z.string().min(1).nullable().default(null),
    terminalId: zod_1.z.string().min(1).nullable().default(null),
    locationId: zod_1.z.string().min(1),
    occurredAt: zod_1.z.string().datetime(),
    idempotencyKey: zod_1.z.string().min(1).max(128).nullable().default(null),
    correlationId: zod_1.z.string().min(1).max(128).nullable().default(null),
    /** Non-secret only. Never a PAN, a token, or anything a receipt would redact. */
    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).default({}),
});
//# sourceMappingURL=self-order.js.map